import { Chip, Stack } from "@mui/material";
import {
  MULTIPLE_VALUES_INDICATOR,
  SkuQualificationGroupModel,
  SkuQualificationRow,
} from "helpers/skuQualificationHelpers";

// Above this many distinct values the axis collapses to a single "N <noun>" counter chip
// (the hi-fi's "47 state boards"); at or below it, each value is shown as a named chip.
const MAX_NAMED_CHIPS = 5;

type RollupCellProps = {
  row: SkuQualificationGroupModel;
  // Pre-aggregated value from the engine; MULTIPLE_VALUES_INDICATOR when members disagree.
  value: unknown;
  // Reads the same axis off one member row (fanned to LB x AB x SA).
  getMemberValue: (member: SkuQualificationRow) => string | undefined;
  // Plural noun for the counter chip, e.g. "47 licensure bodies".
  countNoun: string;
  // Distinct count computed by the caller (by id), used for the threshold instead of the names.
  count?: number;
};

// Rolled-up identity axis (LB / AB / SA) on a grouped row: named chips when few, a counter
// chip when many. A single value stays text.
export const RollupCell = ({
  row,
  value,
  getMemberValue,
  countNoun,
  count,
}: RollupCellProps) => {
  if (count !== undefined && count > MAX_NAMED_CHIPS) {
    return <Chip size="small" label={`${count} ${countNoun}`} />;
  }

  if (value !== MULTIPLE_VALUES_INDICATOR) {
    return <>{value == null ? "" : String(value)}</>;
  }

  const members = row.isGroup ? row.childQualifications : [];
  const values = members.map(getMemberValue);
  const distinct = Array.from(
    new Set(values.filter((v): v is string => !!v && v.trim() !== ""))
  ).sort((a, b) => a.localeCompare(b));

  if (distinct.length === 0) return null;
  // Members with no value are ignored, so one remaining value reads as plain text.
  if (distinct.length === 1) return <>{distinct[0]}</>;
  if (distinct.length > MAX_NAMED_CHIPS) {
    return <Chip size="small" label={`${distinct.length} ${countNoun}`} />;
  }
  return (
    <Stack direction="row" spacing={0.5} useFlexGap flexWrap="wrap">
      {distinct.map((v) => (
        <Chip key={v} size="small" label={v} />
      ))}
    </Stack>
  );
};
